import React, { useState } from 'react';
import { Button, Form, Input, Row, Col, message } from 'antd';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PropTypes } from 'prop-types';
import './stripeCardForm.scss';

const FormItem = Form.Item;

const ELEMENT_STYLE = {
    base: {
        color: '#fff',
        fontFamily: 'Inter, sans-serif',
        fontSize: '16px',
        fontWeight: '400',
        letterSpacing: '0.02em',
        '::placeholder': {
            color: '#B6B6B6',
            fontWeight: '400',
        },
    },
    invalid: {
        color: '#ff4d4f',
    },
};

const StripeCardForm = ({ onSubmit, loading, buttonLabel, language }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardError, setCardError] = useState(null);
    const [form] = Form.useForm();

    const handleSubmit = async (values) => {
        if (!stripe || !elements) {
            return;
        }

        setCardError(null);

        const cardNumberElement = elements.getElement(CardNumberElement);
        const { token, error } = await stripe.createToken(cardNumberElement, {
            name: values.holders_name,
        });

        if (error) {
            setCardError(error.message);
            message.error(error.message);
            return;
        }

        onSubmit({ token: token.id, card: token.card });
    };

    const handleCardChange = (event) => {
        if (event.error) {
            setCardError(event.error.message);
        } else {
            setCardError(null);
        }
    };

    return (
        <Form form={form} onFinish={handleSubmit} layout="vertical" className="form payment-form">
            <div className="payment-form-content-wrapper">
                <FormItem label={language?.cardNumberLabel || 'Card Number:'}>
                    <div className="stripe-element-container">
                        <CardNumberElement
                            options={{ style: ELEMENT_STYLE, placeholder: '0000 0000 0000 0000' }}
                            onChange={handleCardChange} />
                    </div>
                </FormItem>
                <FormItem
                    name="holders_name"
                    label={language?.holdersNameLabel || "Cardholder's Name:"}
                    rules={[{ required: true, message: language?.holdersNameRequired || "Please enter Cardholder's Name!" }]}>
                    <Input size="large" className="payment-input" placeholder="ex. Jonathan Paul Ive" />
                </FormItem>
                <Row gutter={0} align="middle">
                    <Col span={11}>
                        <FormItem label={language?.expiryLabel || 'Expiry Date'}>
                            <div className="stripe-element-container">
                                <CardExpiryElement
                                    options={{ style: ELEMENT_STYLE }}
                                    onChange={handleCardChange} />
                            </div>
                        </FormItem>
                    </Col>
                    <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span className="expiry-slash">/</span>
                    </Col>
                    <Col span={11}>
                        <FormItem label={language?.cvcLabel || 'Security Code:'}>
                            <div className="stripe-element-container">
                                <CardCvcElement
                                    options={{ style: ELEMENT_STYLE, placeholder: '3-4 digits' }}
                                    onChange={handleCardChange} />
                            </div>
                        </FormItem>
                    </Col>
                </Row>
                {cardError && <div className="stripe-card-error">{cardError}</div>}
            </div>
            <div className="text-center mt-5">
                <Button
                    type="primary"
                    htmlType="submit"
                    className="ant-btn-yellow"
                    loading={loading}
                    disabled={!stripe}>
                    {buttonLabel || 'Pay'}
                </Button>
            </div>
        </Form>
    );
};

StripeCardForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    buttonLabel: PropTypes.string,
    language: PropTypes.shape({
        cardNumberLabel: PropTypes.string,
        holdersNameLabel: PropTypes.string,
        holdersNameRequired: PropTypes.string,
        expiryLabel: PropTypes.string,
        cvcLabel: PropTypes.string,
    }),
};

StripeCardForm.defaultProps = {
    loading: false,
    buttonLabel: 'Pay',
    language: {},
};

export default StripeCardForm;
